// MobileMenu.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useLocation, useNavigate } from 'react-router-dom';

// Utilities
import { formatBaseLockedNotice } from 'utilities/baseAccessCopy';
import { SectionType } from 'utilities/Enums';
import { getSectionPath, inferSectionFromPath } from 'utilities/Routing';
import { isBaseLauncherKey, openBaseLauncher } from 'components/addmodal/utilities/baseLaunchers';

// Services
import {
  isBaseBillingInGracePeriod,
  isBaseBillingNavigationLocked,
} from 'services/billing/BaseBillingService';

// Hooks
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';
import useBaseAccessLock from 'hooks/useBaseAccessLock';
import useBaseCreateModals from 'main/sidebar/buckets/hooks/useBaseCreateModals';

// CSS
import './MobileMenu.css';

// Components
import MobileMenuAddButton from './addbutton/MobileMenuAddButton';
import MobileMenuButton from './button/MobileMenuButton';
import AddModal from 'components/addmodal/AddModal';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import SidebarBucketsModals from 'main/sidebar/buckets/SidebarBucketsModals';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';

function formatBillingLockNotice(base, record, lockReason) {

  if (lockReason === 'trial_expired') {
    return formatBaseLockedNotice();
  }

  if (!record) return '';

  const gracePeriodEndsAt = record?.gracePeriodEndsAt instanceof Date
    ? record.gracePeriodEndsAt
    : null;

  if (isBaseBillingNavigationLocked(record)) {
    return 'Billing access is locked. Open base settings to update payment.';
  }

  if (!isBaseBillingInGracePeriod(record)) {
    return '';
  }

  if (!gracePeriodEndsAt) {
    return 'Billing issue detected. Update payment to avoid lockout.';
  }

  return `Billing issue detected. Update payment before ${gracePeriodEndsAt.toLocaleString()}.`;
}

/**
 * MobileMenu
 * 
 * Renders the mobile menu with navigation buttons and add node functionality.
 * 
 * @returns {ReactNode} MobileMenu component.
 */
const MobileMenu = () => {

  // ----- CONTEXT ----- //

  // Global Context

  const {
    setCurrentNode,
    setCurrentSection,
  } = useNavigation();

  const { setActiveDatabase } = useDatabase();

  // User Context
  const {
    user,
    userUnreadNotifications,
    userSettings,
  } = useUser();

  // Theme Context
  const { theme } = useTheme();

  const { getType } = useNodeTypes();
  const { activeBase } = useBase();
  const language = useCurrentLanguage();

  // ----- ROUTING ----- //

  // Routing

  const navigate = useNavigate();
  const location = useLocation();

  // ----- STATE ----- //

  const [addModalOpen, setAddModalOpen] = useState(false);

  // ----- HOOKS ----- //

  const {
    accessLocked: baseNavigationLocked,
    billingRecord,
    lockReason,
  } = useBaseAccessLock();
  const {
    cameraOpen,
    cameraContentType,
    uploadInputRef,
    pendingUploadAccept,
    pendingUploadMultiple,
    handleTopLevelActionSelect,
    handleUploadFileSelect,
    setCameraOpen,
    handleCameraContentCreated,
  } = useTopLevelNodeCreate({
    user,
    activeBaseKey: activeBase?.key || null,
    getType,
    language,
    navigate,
    setCurrentNode,
    setCurrentSection,
    setActiveDatabase,
  });

  const {
    modalState: createModalState,
    openers: createModalOpeners,
  } = useBaseCreateModals();

  // ----- MEMOS ----- //

  const preferredBaseKeyFromSettings = getActiveBaseKeyFromSettings(userSettings);
  const activeBaseKey = useMemo(() => {
    const key = activeBase?.key || activeBase?.id || preferredBaseKeyFromSettings || '';
    return String(key).trim();
  }, [activeBase?.id, activeBase?.key, preferredBaseKeyFromSettings]);

  /**
   * Calculates the number of unread notifications.
   */
  const notificationsUnreadCount = useMemo(() => {
    if (!user || !activeBaseKey || !Array.isArray(userUnreadNotifications)) return 0;

    return userUnreadNotifications
      .filter((notification) => {
        if (!notification || notification.isRead === true) return false;

        const notificationBaseKey = String(notification?.baseKey || '').trim();
        return notificationBaseKey === activeBaseKey;
      })
      .length;
  }, [activeBaseKey, user, userUnreadNotifications]);

  const activeSection = useMemo(() => {
    return inferSectionFromPath(location.pathname) || SectionType.HOME;
  }, [location.pathname]);

  const baseBillingNotice = useMemo(() => {
    return formatBillingLockNotice(activeBase, billingRecord, lockReason);
  }, [activeBase, billingRecord, lockReason]);

  /**
   * Menu Items
   */
  // Find lives in MobileTopBar — no Search destination in the bottom nav.
  const menuItems = useMemo(() => ([
    {
      key: 'home',
      section: SectionType.HOME,
      iconKey: 'home',
      selectedIconKey: 'home_fill',
      title: 'Home',
    },
    { key: 'add', type: 'add' },
    {
      key: 'notifications',
      section: SectionType.NOTIFICATIONS,
      iconKey: 'notifications',
      selectedIconKey: 'notifications',
      title: 'Notifications',
      count: notificationsUnreadCount,
    },
  ]), [notificationsUnreadCount]);

  // ----- HANDLERS ----- //

  /**
 * Handles locked navigation attempt.
 */

  const handleLockedNavigationAttempt = useCallback(() => {
    setActiveDatabase(null);
    setCurrentNode(null);
    setCurrentSection(SectionType.HOME);

    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, setActiveDatabase, setCurrentNode, setCurrentSection]);

  /**
   * Handles Home Click
   */
  const handleNavigate = useCallback((section) => {
    if (baseNavigationLocked && section !== SectionType.HOME) {
      handleLockedNavigationAttempt();
      return;
    }

    if (section !== SectionType.NODE) {
      setCurrentNode(null);
    }

    const targetPath = getSectionPath(section);

    setCurrentSection(section);
    navigate(targetPath);
  }, [handleLockedNavigationAttempt, navigate, setCurrentNode, setCurrentSection, baseNavigationLocked]);

  /**
   * Handles when new nodes are created (from NodeAdd).
   */
  /**
   * Handles click on the add node button.
   */
  const handleAddNodeClick = useCallback(() => {
    if (baseNavigationLocked) {
      handleLockedNavigationAttempt();
      return;
    }

    setAddModalOpen(true);
  }, [handleLockedNavigationAttempt, baseNavigationLocked]);

  /**
 * Handles attachment select.
 *
 * @param {*} typeKey
 */
  const handleAttachmentSelect = useCallback((typeKey, options = {}) => {
    if (baseNavigationLocked) {
      handleLockedNavigationAttempt();
      return;
    }

    if (isBaseLauncherKey(typeKey)) {
      openBaseLauncher(typeKey, createModalOpeners);
      return;
    }

    setAddModalOpen(false);
    handleTopLevelActionSelect(typeKey, {
      initialFields: options?.initialFields || null,
    });
  }, [createModalOpeners, handleLockedNavigationAttempt, handleTopLevelActionSelect, baseNavigationLocked]);

  /**
 * Handles close add modal.
 */
  const handleCloseAddModal = useCallback(() => {
    setAddModalOpen(false);
  }, []);

  const handleBaseCreateCompleted = useCallback(() => {
    setAddModalOpen(false);
  }, []);

  // ----- RENDER ----- //

  return (
    <>

      {/* SHELL */}
      <div className="mobile-menu-shell">

        {/* BILLING ALERT */}
        {baseBillingNotice && (
          <div
            className="mobile-menu-billing-alert"
            style={{
              backgroundColor: baseNavigationLocked
                ? (theme.warning || theme.backgroundHover)
                : theme.backgroundHover,
              borderColor: baseNavigationLocked
                ? (theme.warning || theme.backgroundHover)
                : theme.backgroundHover,
            }}
          >
            {baseBillingNotice}
          </div>
        )}

        <div className="mobile-menu-container">

          {menuItems.map((item) => {

            if (item.type === 'add') {
              return (
                <MobileMenuAddButton
                  key={item.key}
                  enabled={!baseNavigationLocked}
                  onClick={handleAddNodeClick}
                />
              );
            }

            const isLockedTarget = baseNavigationLocked && item.section !== SectionType.HOME;
            const button = (
              <MobileMenuButton
                icon={item.icon}
                iconKey={item.iconKey}
                size={item.size}
                selectedIcon={item.selectedIcon}
                selectedIconKey={item.selectedIconKey}
                color={activeSection === item.section ? theme.foreground : theme.foregroundMuted}
                count={item.count ?? 0}
                enabled={!isLockedTarget}
                title={item.title}
                selected={activeSection === item.section}
                onClick={() => handleNavigate(item.section)}
              />
            );

            return React.cloneElement(button, { key: item.key });
          })}

        </div>
      </div>

      <input
        style={{
          display: 'none',
        }}
        ref={uploadInputRef}
        type="file"
        accept={pendingUploadAccept}
        multiple={pendingUploadMultiple}
        onChange={handleUploadFileSelect}
      />

      <AddModal
        isOpen={addModalOpen}
        showFind={false}
        showBaseLaunchers
        onClose={handleCloseAddModal}
        onTypeSelect={handleAttachmentSelect}
      />

      <SidebarBucketsModals
        modalState={createModalState}
        onCreated={handleBaseCreateCompleted}
      />

      <CameraModal
        isOpen={cameraOpen}
        setOpen={setCameraOpen}
        contentType={cameraContentType}
        onContentCreated={handleCameraContentCreated}
      />

    </>
  );
};

// Dev Tools
MobileMenu.displayName = 'MobileMenu';

export default MobileMenu;
